import type { AnalyticsEventPropertiesMap, AnalyticsEvents } from './events';
import { captureAnalyticsEvent } from './events';

/**
 * FFH-2372 — "one view per page visit" guard for view-type events.
 *
 * Mount-once effects are not visit-once: the embedded article paywall and the
 * funnel PaywallBlock remount several times within seconds of a page load
 * (auth and profile resolving, marketing copy arriving, tier reselects), and
 * in production `paywall_viewed` shipped 4-6 times per session on a single
 * page. This module remembers what has already been captured for the current
 * page visit and swallows the repeats.
 *
 * "Page visit" means a document load or a non-shallow Next router navigation;
 * `resetOncePerPageAnalytics` is called from `PostHogPageviewTracker` on
 * `routeChangeStart`, the same boundary pageviews are counted on. Shallow
 * route changes and the funnels' raw `pushState` hash steps do not reset it,
 * on purpose: they are within-page state, and the pageview does not re-fire
 * for them either.
 */
const capturedKeys = new Set<string>();

const buildKey = (eventName: string, dedupeKey: string): string =>
  `${eventName}|${dedupeKey}`;

/**
 * Captures `eventName` unless the same event with the same `dedupeKey` has
 * already been captured during this page visit. Returns whether it captured.
 * Pre-consent captures count as captured: they sit in the pending queue and
 * are flushed or discarded by the consent settle, so re-capturing later would
 * double them.
 */
export const captureAnalyticsEventOncePerPage = <
  EventName extends AnalyticsEvents
>(
  eventName: EventName,
  properties: AnalyticsEventPropertiesMap[EventName],
  dedupeKey: string
): boolean => {
  const key = buildKey(eventName, dedupeKey);
  if (capturedKeys.has(key)) return false;

  capturedKeys.add(key);
  captureAnalyticsEvent(eventName, properties);
  return true;
};

/** Forgets everything captured for the page visit that is ending. */
export const resetOncePerPageAnalytics = (): void => {
  capturedKeys.clear();
};
